import cn from 'classnames';
import dateFnsFormat from 'date-fns/format';
import dateFnsParseISO from 'date-fns/parseISO';
import {defaultDateFormat} from "~/theme";
import Cookie from "js-cookie";

const shortCountriesList = require('~/countriesShortList.json');
const currenciesList = require('~/currencies.json');

export function isDev() {
  return Cookie.get('_use') === 'dev';
}


export function createUniqueId() {
  return Math.random().toString(36).substr(2, 10);
}

export function backendUrl(url) {
  return process.env.BACKEND_URL + url;
}

export function externalUrl(url) {
  return process.env.BACKEND_URL + url;
}

export function clientStorageSet(key, value, expires = -1) {
  return localStorage.setItem(key, buildQueryString({
    v: JSON.stringify(value),
    expire: (expires !== -1) ? (Number(Date.now()) + expires) : -1
  }));
}

export function clientStorageGet(key) {
  if (!localStorage.getItem(key)) {
    return null;
  }

  try {
    let {v, expire} = parseQueryString(localStorage.getItem(key));

    expire = parseInt(expire, 10);
    if (expire !== -1 && expire < Date.now()) {
      localStorage.removeItem(key);
      return null;
    }

    return JSON.parse(v);
  } catch (e) {
    return null;
  }

}

export function getCurrenciesList() {
  return currenciesList;
}

export function getShortCountriesList() {
  return shortCountriesList;
}

export function getFullCountriesList() {
  return import(/* webpackChunkName: "countries" */ '~/countries.json')
}

export function nodeDeepContains(parent, node) {
  while (node && node !== document.body && !parent.contains(node)) {
    node = node.parentNode;
  }
  return parent.contains(node);
}

export function dateFormat(date, format = defaultDateFormat) {
  if (typeof date === 'string') {
    date = dateFnsParseISO(date);
  }

  return dateFnsFormat(date, format);
}

function safeEncodeURIComponent(s) {
  return encodeURIComponent(s).replace(/%2F/g, '/').replace(/%3A/g, ':');
}

export function parseQueryString(qs) {
  qs = qs ? qs.split('&') : [];

  const params = {};

  for (let i = 0; i < qs.length; i++) {
    const [key, ...values] = qs[i].split('=');
    const value = values.join('=');
    params[decodeURIComponent(key)] = decodeURIComponent(value);
  }

  return params;
}

export function buildQueryString(params) {
  return Object.entries(params).filter(([key, value]) => value !== undefined).map(([key, value]) => {
    if (value === null) {
      return '';
    }

    return safeEncodeURIComponent(key) + '=' + safeEncodeURIComponent(value);
  }).filter(v => !!v).join('&');
}

export function getQueryParams(history) {
  return parseQueryString(history.location.search.substring(1));
}

export function getQueryParam(history, paramName) {
  const qs = history.location.search.substring(1).split('&');

  for (let i = 0; i < qs.length; i++) {
    const [key, ...values] = qs[i].split('=');
    const value = values.join('=');

    if (decodeURIComponent(key) === paramName) {
      return decodeURIComponent(value);
    }
  }

  return undefined;
}

export function setQueryParams(history, params, replace = true) {
  let query = history.location.search.substring(1);
  query = query ? query.split('&') : [];

  for (let i = 0; i < query.length; i++) {
    const [key] = query[i].split('=');
    if (decodeURIComponent(key) in params) {
      query[i] = (params[key] !== null) ? (key + '=' + encodeURIComponent(params[key])) : false;
      delete params[key];
    }
  }

  Object.entries(params).forEach(([paramName, paramValue]) => {
    query.push((paramValue !== null) ? (encodeURIComponent(paramName) + '=' + encodeURIComponent(paramValue)) : false);
  });

  query = query.filter((item) => (item !== false));

  (replace ? history.replace : history.push)({
    search: query.length > 0 ? '?' + query.join('&') : ''
  });
}

export function updateQueryParams(history, params) {
  const update = {};
  Object.entries(params).forEach(([paramName, value]) => {
    value = value.toString();

    const currentValue = getQueryParam(history, paramName);
    if (currentValue !== value) {
      update[paramName] = value;
    }
  });

  setQueryParams(history, update, true);
}

export function createSetQueryParamUrl(history, setQueryParams) {
  let nextQueryParams = getQueryParams(history);

  Object.entries(setQueryParams).forEach(([name, value]) => {
    nextQueryParams[name] = value;

    if (value === null) {
      delete nextQueryParams[name];
    }
  });

  return '?' + buildQueryString(nextQueryParams);
}

export function truncateString(string, maxChars, ending = '...') {
  if (string.length > maxChars) {
    return string.substring(0, maxChars - ending.length) + ending;
  }

  return string;
}


export function getSafeURL(url, defaultUrl = '/') {
  return url && url.startsWith('/') ? url : defaultUrl;
}

export function getEnvironment() {
  return process.env.NODE_ENV;
}


export function getHostEnvironment() {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }

  if (hostname === 'app.workshopbutler.com') {
    return 'production';
  }

  if (hostname.endsWith('.workshopbutler.com')) {
    return hostname;
  }

  return '[NotImplemented]';
}

function clearValidationMessageHandler(e) {
  e.target.setCustomValidity("");

}

export function showInputValidationMessage(el, message) {
  el.setCustomValidity(message);
  el.reportValidity();

  el.addEventListener("input", clearValidationMessageHandler, {once: true});
  el.addEventListener("blur", clearValidationMessageHandler, {once: true});
}

export function clearInputValidationMessage(el) {
  el.setCustomValidity("");
  el.removeEventListener("input", clearValidationMessageHandler);
  el.removeEventListener("blur", clearValidationMessageHandler);
}

export function extendClasses(classes, ...extraClasses) {
  extraClasses.filter(classes => !!classes).forEach((extraClasses) => {
    Object.entries(extraClasses).forEach(([key, extraClassName]) => {
      classes[key] = cn(classes[key], extraClassName);
    })
  });

  return classes;
}

export function getReactElementInnerText(el) {
  if (el === null || el === false || typeof el === 'undefined') {
    return '';
  }

  if (typeof el === 'number' || typeof el === 'boolean') {
    return Number(el).toString();
  }

  if (typeof el === 'string') {
    return el;
  }

  if (Array.isArray(el)) {
    return el.reduce((previous, current) => {
      return previous + getReactElementInnerText(current)
    }, '');
  }

  if (Object.prototype.hasOwnProperty.call(el, 'props') &&
    Object.prototype.hasOwnProperty.call(el.props, 'children')) {
    return getReactElementInnerText(el.props.children);
  }

  return '';
}


export function range(n) {
  const l = [];
  for (let i = 0; i < n; i++) {
    l.push(i);
  }

  return l;
}

export function isDarkColor(color) {
  color = color.replace('#', '');
  if (color.length === 3) color = color.split('').map(l=>l+l).join('');

  return ((
    (parseInt(color.slice(0, 2), 16) * 0.299) + // hexToR
    (parseInt(color.slice(2, 4), 16) * 0.587) + // hexToG
    (parseInt(color.slice(4, 6), 16) * 0.114) // hexToB
  ) < 186);
}
